/* sb-principle-tabs.js — manual tab controller for the principle band.
   The scroll-driven full-screen lock is retired (owner direction 2026-07-23,
   same day it shipped): the pillars switch by click/tap only, at every
   width. This script owns the chip/panel/slide .is-active sync, the
   roving-tabindex keyboard pattern (arrows/Home/End), and the swipe (owner
   direction 2026-07-24, the AG1 move): copy and photograph travel together
   — the incoming pair arrives from the direction of travel, the outgoing
   pair leaves the opposite way (direction classes map to keyframes in the
   section) — under a horizontal motion blur whose σ this script decays to
   zero over the 0.7s run so travellers land sharp. Reduced motion gets the
   instant swap via the CSS gates (which also withhold the blur filter);
   no-JS never hides a panel (hiding is gated on .sb-js in the section). */
(function () {
  'use strict';

  var SWIPE_CLASSES = ['is-in-from-right', 'is-in-from-left', 'is-out-to-left', 'is-out-to-right'];
  var BLUR_MAX = 9; /* peak horizontal σ, px — "a touch", not a smear */
  var BLUR_MS = 700; /* matches the CSS swipe run */

  function stripSwipe(els) {
    els.forEach(function (el) {
      SWIPE_CLASSES.forEach(function (c) { el.classList.remove(c); });
    });
  }

  function init(band) {
    if (band.dataset.sbPtabsInit) return;
    band.dataset.sbPtabsInit = '1';

    var tabs = [].slice.call(band.querySelectorAll('[data-sb-ptabs-tab]'));
    var panels = [].slice.call(band.querySelectorAll('[data-sb-ptabs-panel]'));
    var slides = [].slice.call(band.querySelectorAll('[data-sb-ptabs-slide]'));
    var mlinks = [].slice.call(band.querySelectorAll('[data-sb-ptabs-mlink]'));
    var blurNode = band.querySelector('[data-sb-ptabs-blur]');
    if (!tabs.length || tabs.length !== panels.length) return;

    /* ≤749 pillar deck (owner-approved A1, 2026-07-28): the chips become a
       pager for the scroll-snap card deck — tap scrolls the deck, swiping
       the deck moves the chip/dots/counter. The desktop sweep+blur
       machinery below never runs in deck mode. */
    var deck = band.querySelector('[data-sb-ptabs-deck]');
    var dcards = deck ? [].slice.call(deck.querySelectorAll('[data-sb-ptabs-dcard]')) : [];
    var ddots = [].slice.call(band.querySelectorAll('[data-sb-ptabs-ddots] i'));
    var dcount = band.querySelector('[data-sb-ptabs-dcount]');
    var deckQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    var deckScrollTimer = 0;
    var deckRaf = 0;
    var deckLefts = [];
    var deckPitch = 0;
    var deckPeek = 0;
    var deckSynced = -1;
    var deckPending = -1;

    var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var current = 0;
    var swipeTimer = 0;
    var blurRaf = 0;

    function deckMode() {
      return !!(deck && dcards.length && deckQuery && deckQuery.matches);
    }

    /* The deck's own active index, read straight from scroll position —
       card pitch is the distance between the first two cards. */
    function deckIndex() {
      if (dcards.length < 2) return 0;
      var pitch = dcards[1].offsetLeft - dcards[0].offsetLeft;
      if (pitch <= 0) return 0;
      var i = Math.round(deck.scrollLeft / pitch);
      return Math.max(0, Math.min(dcards.length - 1, i));
    }

    function deckSync(i) {
      deckSynced = i;
      tabs.forEach(function (t, ti) {
        var on = ti === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) t.removeAttribute('tabindex');
        else t.setAttribute('tabindex', '-1');
      });
      ddots.forEach(function (d, di) { d.classList.toggle('is-on', di === i); });
      if (dcount) dcount.textContent = (i + 1) + ' / ' + dcards.length;
    }

    /* Feather + focus-pull measurements (owner request 2026-07-29): the
       peek — how much viewport shows beyond the settled card — bounds the
       feather so the mask ramp only ever covers the sliver of the unread
       neighbour, never the settled card itself; the ¾ factor is the
       owner's same-day tune (the full-peek ramp read too heavy). Re-measured
       on resize; at ≥750 the deck is display:none and everything reads 0,
       which leaves the effect inert. */
    function deckMeasure() {
      if (!deck || !dcards.length) return;
      var dr = deck.getBoundingClientRect();
      var cr = dcards[0].getBoundingClientRect();
      var base = dcards[0].offsetLeft;
      deckLefts = dcards.map(function (c) { return c.offsetLeft - base; });
      deckPitch = dcards.length > 1 ? deckLefts[1] : 0;
      deckPeek = Math.max(0, dr.width - (cr.left - dr.left + deck.scrollLeft) - cr.width) * 0.75;
    }

    /* Dress the deck to its scroll position, one write per frame — RIGHT
       edge only (owner tune 2026-07-29: the already-read sliver at the
       left stays plain): the feather collapses to zero at the rail's end
       (nothing hides behind it there) and each UNREAD card's blur rides
       its distance AHEAD of the snap position — signed, so the card just
       read keeps its sharpness as it leaves. Reduced motion strips the
       props instead — the CSS defaults render the plain sharp deck. */
    function deckDress() {
      deckRaf = 0;
      if (!deck || !dcards.length) return;
      if (reduceQuery && reduceQuery.matches) {
        deck.style.removeProperty('--sb-ptabs-fr');
        dcards.forEach(function (c) { c.style.removeProperty('--sb-ptabs-doff'); });
        return;
      }
      var sl = deck.scrollLeft;
      var maxSl = Math.max(0, deck.scrollWidth - deck.clientWidth);
      deck.style.setProperty('--sb-ptabs-fr', Math.max(0, Math.min(maxSl - sl, deckPeek)).toFixed(1) + 'px');
      dcards.forEach(function (c, ci) {
        /* The rail ends short of the last card's snap position (86% cards
           leave less than a viewport after its start), so its distance is
           measured to the nearest REACHABLE scroll — otherwise the settled
           last card keeps a residual blur it can never shed (owner report
           2026-08-12: Support stayed soft). */
        var target = Math.min(deckLefts[ci], maxSl);
        var p = deckPitch > 0 ? Math.min(1, Math.max(0, (target - sl) / deckPitch)) : 0;
        c.style.setProperty('--sb-ptabs-doff', p.toFixed(3));
      });
    }

    function deckDressSoon() {
      if (deckRaf || !window.requestAnimationFrame) return;
      deckRaf = requestAnimationFrame(deckDress);
    }

    function deckGo(i, focusTab) {
      i = Math.max(0, Math.min(dcards.length - 1, i));
      deckPending = i;
      var left = dcards[i].offsetLeft - dcards[0].offsetLeft;
      var smooth = !(reduceQuery && reduceQuery.matches);
      if (deck.scrollTo) deck.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
      else deck.scrollLeft = left;
      deckSync(i);
      if (focusTab) tabs[i].focus();
    }

    /* One shared feGaussianBlur per band: every swipe restarts its σ at
       the peak and decays it quadratically — high blur while the ease is
       fast, zero by landing — so the CSS filter never pops off sharp. */
    function runBlur() {
      if (!blurNode || !window.requestAnimationFrame) return;
      cancelAnimationFrame(blurRaf);
      var start = 0;
      function step(now) {
        if (!start) start = now;
        var t = Math.min(1, (now - start) / BLUR_MS);
        var sigma = BLUR_MAX * (1 - t) * (1 - t);
        blurNode.setAttribute('stdDeviation', sigma.toFixed(2) + ' 0');
        if (t < 1) blurRaf = requestAnimationFrame(step);
      }
      blurRaf = requestAnimationFrame(step);
    }

    function activate(i, focusTab) {
      i = Math.max(0, Math.min(tabs.length - 1, i));
      /* Re-clicking the active chip is a no-op (the AG1 behaviour) —
         keyboard Home/End at the boundary still lands focus. */
      if (i === current) {
        if (focusTab) tabs[i].focus();
        return;
      }
      var forward = i > current;
      var outClass = forward ? 'is-out-to-left' : 'is-out-to-right';
      var inClass = forward ? 'is-in-from-right' : 'is-in-from-left';
      var leavingPanel = panels[current];
      var leavingSlide = slides[current];
      current = i;

      tabs.forEach(function (t, ti) {
        var on = ti === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) t.removeAttribute('tabindex');
        else t.setAttribute('tabindex', '-1');
      });
      /* Copy and photograph travel as one: strip any mid-flight direction
         classes, flip .is-active, then send each pair off. The offsetWidth
         reads force a style flush so a re-added animation restarts. */
      stripSwipe(panels);
      stripSwipe(slides);
      panels.forEach(function (p, pi) { p.classList.toggle('is-active', pi === i); });
      slides.forEach(function (sl, si) { sl.classList.toggle('is-active', si === i); });
      if (leavingPanel) leavingPanel.classList.add(outClass);
      if (leavingSlide) leavingSlide.classList.add(outClass);
      void panels[i].offsetWidth;
      panels[i].classList.add(inClass);
      if (slides[i]) {
        void slides[i].offsetWidth;
        slides[i].classList.add(inClass);
      }
      if (!(reduceQuery && reduceQuery.matches)) runBlur();
      clearTimeout(swipeTimer);
      swipeTimer = setTimeout(function () {
        stripSwipe(panels);
        stripSwipe(slides);
      }, 750);
      /* Mobile bottom link mirrors the active panel's link (indices are
         block indices — blocks without a link render no element). */
      mlinks.forEach(function (l) {
        l.classList.toggle('is-active', parseInt(l.getAttribute('data-sb-ptabs-mlink'), 10) === i);
      });
      if (focusTab) tabs[i].focus();
    }

    /* One entry point per gesture: deck mode pans the card deck, desktop
       mode runs the panel/slide swipe theatre. */
    function go(i, focusTab) {
      if (deckMode()) deckGo(i, focusTab);
      else activate(i, focusTab);
    }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { go(i); });
      t.addEventListener('keydown', function (e) {
        var to = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') to = 0;
        else if (e.key === 'End') to = tabs.length - 1;
        if (to < 0) return;
        e.preventDefault();
        go(to, true);
      });
    });

    /* Swiping the deck drives the pager back LIVE — the chip takes its
       colour the moment the nearest card changes, mid-gesture, not after
       the snap settles (owner request 2026-08-12). A chip TAP still owns
       the pager until its smooth scroll lands (deckPending), so travelling
       past a card never flashes its chip on the way through; the debounce
       stays as the settle pass that releases that hold. Display:none kills
       scroll events ≥750, so this listener is inert outside deck mode. */
    if (deck) {
      deck.addEventListener('scroll', function () {
        deckDressSoon();
        if (deckPending < 0) {
          var di = deckIndex();
          if (di !== deckSynced) deckSync(di);
        }
        clearTimeout(deckScrollTimer);
        deckScrollTimer = setTimeout(function () {
          deckPending = -1;
          deckSync(deckIndex());
        }, 80);
      }, { passive: true });
      window.addEventListener('resize', function () {
        deckMeasure();
        deckDressSoon();
      }, { passive: true });
      deckMeasure();
      deckDress();
    }
  }

  function boot(root) {
    var bands = (root || document).querySelectorAll('[data-sb-ptabs]');
    for (var i = 0; i < bands.length; i++) init(bands[i]);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
